import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Card, Col, Input, Row, Space, Table } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { CardTitle, StatCard } from '../../components/StatCard';
import { NameCell, TableSearch } from '../../components/NameCell';
import type { ContactListItem, RuleGroup } from '@crm/shared';
import { emptyRuleGroup } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { FilterBuilder } from '../../components/FilterBuilder';
import { useListParams } from '../../components/list-params';
import { downloadCsv } from '../../components/export-csv';
import { ExportButton } from '../../components/ExportButton';
import { useFields } from '../../features/fields/api';
import { contactName } from '../../features/contacts/api';
import { useAudienceData, useCreateSegment, useSegment, useSegmentContacts, useSegmentPreview, useUpdateSegment } from '../../features/audience/api';
import { errorMessage } from '../../api/client';
import { useVerifySelection } from '../../features/verification/api';
import { renderCell } from '../contacts/ContactsPage';
import { PageSkeleton, RecordError, tableLoading, tableLocale } from '../../components/states';
import { plural } from '../../utils/format';
import { ProjectSelect } from '../../components/ProjectSelect';
import { useProjectScope } from '../../features/projects/scope';

export function SegmentEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { message } = App.useApp();
  const segment = useSegment(isNew ? undefined : id);
  const defs = useFields('CONTACT');
  const orgDefs = useFields('ORGANIZATION');
  const audience = useAudienceData();
  const create = useCreateSegment();
  const update = useUpdateSegment();
  const verifySel = useVerifySelection();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState<RuleGroup>(emptyRuleGroup());
  const scope = useProjectScope();
  const [projectId, setProjectId] = useState<string | null>(null);
  // a new segment starts in the project picked in the header
  const [projectDefaulted, setProjectDefaulted] = useState(!isNew);
  if (!projectDefaulted && scope.ready) {
    setProjectDefaulted(true);
    setProjectId(scope.current?.id ?? null);
  }
  const [loaded, setLoaded] = useState(isNew);
  const preview = useSegmentPreview(rules);
  const { params, setParams } = useListParams({ sortBy: 'last_name', sortDir: 'asc' });
  const members = useSegmentContacts(isNew ? undefined : id, params);

  useEffect(() => {
    if (segment.data && !loaded) {
      setName(segment.data.name);
      setDescription(segment.data.description ?? '');
      setRules(segment.data.rules);
      setProjectId(segment.data.projectId);
      setLoaded(true);
    }
  }, [segment.data, loaded]);

  if (!isNew && segment.isError) return <RecordError error={segment.error} what="Segment" backTo="/segments" backLabel="Back to segments" onRetry={segment.refetch} />;
  if (!loaded) return <PageSkeleton />;

  // segments cannot reference themselves
  const otherSegments = audience.segments.filter((s) => s.id !== id);
  const dirty = isNew
    ? true
    : !!segment.data && (name !== segment.data.name || description !== (segment.data.description ?? '') || projectId !== segment.data.projectId || JSON.stringify(rules) !== JSON.stringify(segment.data.rules));

  const save = async () => {
    if (!name.trim()) return message.warning('Give the segment a name');
    try {
      if (isNew) {
        const s = await create.mutateAsync({ name: name.trim(), description: description || null, rules, ...(scope.enabled && { projectId }) });
        message.success(`Segment created with ${plural(s.memberCount, 'member')}`);
        navigate(`/segments/${s.id}`, { replace: true });
      } else {
        const s = await update.mutateAsync({ id: id!, name: name.trim(), description: description || null, rules, ...(scope.enabled && { projectId }) });
        message.success(`Segment saved with ${plural(s.memberCount, 'member')}`);
      }
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const cols = (defs.data ?? []).filter((d) => ['job_title', 'organization_id', 'lifecycle_stage', 'email_status'].includes(d.key));
  const segFilter = id ? { combinator: 'and' as const, conditions: [{ kind: 'segment' as const, op: 'in_any' as const, segmentIds: [id] }] } : undefined;

  return (
    <div className="page">
      <PageHeader
        breadcrumb={[{ title: 'Segments', to: '/segments' }, { title: isNew ? 'New segment' : segment.data?.name ?? '' }]}
        title={isNew ? 'New segment' : segment.data?.name ?? ''}
        extra={
          <Space>
            {!isNew && <ExportButton onExport={() => downloadCsv('/contacts/export.csv', params, { filter: JSON.stringify(segFilter) })} count={members.data?.total} noun="contact" />}
            {!isNew && segFilter && <Button loading={verifySel.isPending} onClick={() => verifySel.mutateAsync({ selection: { all: { filter: segFilter } }, onlyUnverified: true }).then((j) => message.success(`Queued ${plural(j.total, 'member')} for email verification`)).catch((e) => message.error(errorMessage(e)))}>Verify emails</Button>}
            <Button type="primary" onClick={() => void save()} loading={create.isPending || update.isPending} disabled={!dirty}>
              {isNew ? 'Create segment' : 'Save changes'}
            </Button>
          </Space>
        }
      />
      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card title={<CardTitle title="Definition" subtitle="Name the segment and set the rules contacts must match" />}>
            <Space orientation="vertical" style={{ width: '100%' }} size="middle">
              <Input placeholder="Segment name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
              <Input placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} />
              {scope.enabled && <ProjectSelect value={projectId} onChange={setProjectId} style={{ width: '100%' }} />}
              <FilterBuilder value={rules} onChange={setRules} contactFields={defs.data ?? []} organizationFields={orgDefs.data ?? []} audience={{ ...audience, segments: otherSegments }} />
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <StatCard
            icon={<TeamOutlined />}
            label="Contacts matching now"
            value={!rules.conditions.length ? 0 : preview.isError ? '—' : (preview.data?.count ?? 0).toLocaleString()}
            loading={!!rules.conditions.length && preview.isFetching}
            note={
              !rules.conditions.length
                ? 'No conditions yet. Add one to match contacts, or save it empty and add contacts by hand (e.g. from an import).'
                : preview.isError
                  ? 'Finish each condition to see a count.'
                  : !isNew && segment.data
                    ? `Stored membership: ${segment.data.memberCount.toLocaleString()}${dirty ? ' (rules changed, save to apply)' : ''}`
                    : 'Live count for the rules on the left.'
            }
          />
        </Col>
      </Row>
      {!isNew && (
        <Card title={<CardTitle title={`Members (${(segment.data?.memberCount ?? 0).toLocaleString()})`} subtitle="Contacts matching the rules as of the last refresh, plus any added by an import" />} style={{ marginTop: 24 }}>
          <div className="table-toolbar">
            <div className="toolbar-group">
              <TableSearch value={params.q} onSearch={(q) => setParams({ q: q || undefined })} placeholder="Search members" width={300} />
            </div>
          </div>
          <Table<ContactListItem>
            rowKey="id"
            size="middle"
            loading={tableLoading(members)}
            scroll={{ x: 'max-content' }}
            locale={tableLocale(members, params.q ? 'No members match this search.' : 'No contacts match these rules yet. Membership refreshes every 15 minutes and when you save.')}
            dataSource={members.data?.items ?? []}
            pagination={{ current: params.page, pageSize: params.pageSize, total: members.data?.total ?? 0, onChange: (page, pageSize) => setParams({ page, pageSize }) }}
            columns={[
              { title: 'Name', key: 'name', render: (_, c) => <NameCell name={contactName(c)} sub={c.email} to={`/contacts/${c.id}`} /> },
              ...cols.map((f) => ({ title: f.label, key: f.key, ellipsis: true, render: (_: unknown, c: ContactListItem) => renderCell(f, c) })),
            ]}
          />
        </Card>
      )}
    </div>
  );
}
